import { type FC, useState } from "react";
import { Box, AppBar, TablePagination, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  useCountryAccessesQuery,
  type CountryAccessesItem,
} from "@/partner/entities/countryAccesses";
import { useFilter } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { CountryAccessesListItem } from "./CountryAccessesListItem";
import { CountryAccessesListToolbar } from "./CountryAccessesListToolbar";

interface CountryAccessesListProps {
  onEdit: (id: string) => void;
}

export const CountryAccessesList: FC<CountryAccessesListProps> = ({
  onEdit,
}) => {
  const { t } = useTranslation();

  const {
    onPageChange,
    onRowsPerPageChange,
    params: { page, rowsPerPage },
    filter: { search },
  } = useFilter();

  const { data, loading } = useCountryAccessesQuery({
    variables: {
      page,
      rowsPerPage,
      search,
    },
  });

  const countryAccessesData = data?.countryAccesses;
  const countryAccesses = countryAccessesData?.nodes ?? [];
  const count = countryAccessesData?.count ?? 0;

  const [selectedItems, setSelectedItems] = useState<{ id: string }[]>([]);
  const selectedIds = selectedItems.map((item) => item.id);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedItems(countryAccesses.map((item) => ({ id: item.id })));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectOne = (
    id: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    event.stopPropagation();
    if (selectedIds.includes(id)) {
      setSelectedItems(selectedItems.filter((item) => item.id !== id));
    } else {
      setSelectedItems([...selectedItems, { id }]);
    }
  };

  const numSelected = selectedIds.length;
  const indeterminate = numSelected > 0 && numSelected < countryAccesses.length;

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (countryAccesses.length === 0) {
    return <EmptyListCard title={t("countryAccesses.list.empty")} />;
  }

  return (
    <Box>
      <AppBar position="static" color="default" elevation={0}>
        <CountryAccessesListToolbar
          indeterminate={indeterminate}
          numSelected={numSelected}
          nodes={countryAccesses}
          onSelectAllClick={handleSelectAll}
          selectedItems={selectedItems}
          onCloseSelected={() => setSelectedItems([])}
        />
      </AppBar>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
        {countryAccesses.map((item: CountryAccessesItem) => (
          <CountryAccessesListItem
            key={item.id}
            item={item}
            selected={selectedIds.includes(item.id)}
            onSelectOneChange={handleSelectOne}
            onClickItem={onEdit}
          />
        ))}
      </Box>

      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25, 100]}
        labelRowsPerPage={t("common.pagination.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) =>
          t("common.pagination.displayedRows", {
            from,
            to,
            count:
              count !== -1 ? count : t("common.pagination.moreThan", { to }),
          })
        }
      />
    </Box>
  );
};
